import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { hasRole, isStaff, useMe } from '../lib/session';
import { fmtCountdown, fmtTime, useNow } from '../lib/format';
import { audienceText, fmtBytes, LEVEL_LABEL, LEVELS, TYPE_LABEL, type Collection, type ItemType, type LibraryItem } from '../lib/library';
import { Button, Card, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Select } from '../components/ui';
import { LibraryRecordingPanel } from '../components/Recordings';
import { DocumentPanel, FinishedCard, Prose, RequiredLearningAdmin, StatusActions, StatusPill, TypeBadge, VideoPlayer } from '../components/library/parts';

/** INT-LIB-01…05: the training library — videos, documents and strategies, grouped by collection. */

const TYPES: [ItemType | '', string][] = [
  ['', 'Everything'],
  ['VIDEO_LINK', 'Videos'],
  ['DOCUMENT', 'Documents'],
  ['STRATEGY', 'Strategies'],
];

function ItemCard({ i, tz, now, staff, onTag }: { i: LibraryItem; tz: string; now: number; staff: boolean; onTag: (t: string) => void }) {
  const req = i.me.required;
  return (
    <article className="flex min-w-0 flex-col gap-2.5 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-center gap-2">
        <TypeBadge type={i.type} />
        {i.strategy && <span className="font-mono text-xs font-semibold text-sub">{i.strategy.code} · v{i.strategy.currentVersion}</span>}
        {i.level && <span className="text-xs text-muted">{LEVEL_LABEL[i.level]}</span>}
        <span className="ml-auto flex flex-wrap gap-1.5">
          {staff && <StatusPill status={i.status} />}
          {i.me.completedAt ? <Pill tone="up">✓ Finished</Pill> : req ? <Pill tone={req.overdue ? 'down' : 'warn'}>{req.overdue ? 'Required · overdue' : `Required · due ${fmtCountdown(req.dueAt, now)}`}</Pill> : null}
        </span>
      </div>
      <Link to={i.link} className="text-base leading-snug font-semibold text-ink hover:text-accent">
        {i.title}
      </Link>
      {i.description && <p className="m-0 line-clamp-3 text-sm leading-relaxed text-sub">{i.description}</p>}
      {i.file && (
        <span className="truncate text-sm text-sub">
          {i.file.filename} · {fmtBytes(i.file.sizeBytes)}
        </span>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
        {i.tags.map((t) => (
          <button key={t} type="button" onClick={() => onTag(t)} className="cursor-pointer rounded-full border border-line2 bg-raised px-2.5 py-0.5 text-xs text-sub hover:text-ink" aria-label={`Show items tagged ${t}`}>
            #{t}
          </button>
        ))}
      </div>
      <span className="text-xs text-muted">
        {i.createdBy.name} · {fmtTime(i.publishedAt ?? i.createdAt, tz, true)}
        {staff && i.audience ? ` · ${audienceText(i.audience)}` : ''}
      </span>
    </article>
  );
}

export function Library() {
  const me = useMe();
  const staff = isStaff(me.data?.roles);
  const editor = hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN');
  const tz = me.data?.timezone ?? 'UTC';
  const now = useNow(60_000);
  const [q, setQ] = useState('');
  const [type, setType] = useState<ItemType | ''>('');
  const [level, setLevel] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [tag, setTag] = useState('');
  const [status, setStatus] = useState('');
  const params = new URLSearchParams({ ...(q && { q }), ...(type && { type }), ...(level && { level }), ...(collectionId && { collectionId }), ...(tag && { tag }), ...(status && { status }) });
  const items = useQuery({ queryKey: ['library', params.toString()], queryFn: () => api<LibraryItem[]>(`/library?${params}`), placeholderData: (p) => p });
  const collections = useQuery({ queryKey: ['library-collections'], queryFn: () => api<Collection[]>('/library/collections') });

  const groups = useMemo(() => {
    const out: { key: string; title: string; description: string | null; items: LibraryItem[] }[] = [];
    for (const i of items.data ?? []) {
      const key = i.collection?.id ?? '';
      let g = out.find((x) => x.key === key);
      if (!g) {
        const c = collections.data?.find((x) => x.id === key);
        g = { key, title: i.collection?.title ?? 'Not in a collection', description: c?.description ?? null, items: [] };
        out.push(g);
      }
      g.items.push(i);
    }
    return out;
  }, [items.data, collections.data]);

  return (
    <>
      <PageHeader
        title="Library"
        sub="Videos, documents and strategies from the Master Traders"
        right={
          editor && (
            <>
              <Link to="/library/new?type=VIDEO_LINK" className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3.5 py-2.5 text-sm font-semibold text-ink no-underline">
                + Video
              </Link>
              <Link to="/library/new?type=DOCUMENT" className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3.5 py-2.5 text-sm font-semibold text-ink no-underline">
                + Document
              </Link>
              <Link to="/library/strategies/new" className="rounded-lg bg-accent px-3.5 py-2.5 text-sm font-bold text-bg no-underline">
                + Strategy
              </Link>
            </>
          )
        }
      />
      <Card className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4">
        <Field label="Search" className="col-span-2">
          <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, description, tag or strategy code" />
        </Field>
        <Field label="Collection">
          <Select value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
            <option value="">All collections</option>
            {collections.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
            <option value="none">Not in a collection</option>
          </Select>
        </Field>
        <Field label="Level">
          <Select value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Any level</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABEL[l]}
              </option>
            ))}
          </Select>
        </Field>
        <div role="group" aria-label="Type" className="col-span-2 flex flex-wrap items-center gap-1.5 lg:col-span-3">
          {TYPES.map(([v, l]) => (
            <button key={v} type="button" aria-pressed={type === v} onClick={() => setType(v)} className={cx('min-h-9 cursor-pointer rounded-lg border border-line2 px-3 py-1 text-[13px]', type === v ? 'bg-[#1a1f27] text-ink' : 'bg-panel text-sub')}>
              {l}
            </button>
          ))}
          {tag && (
            <button type="button" onClick={() => setTag('')} className="min-h-9 cursor-pointer rounded-full border-0 bg-accent px-3 py-1 text-[13px] font-semibold text-bg" aria-label={`Remove tag filter ${tag}`}>
              #{tag} ✕
            </button>
          )}
        </div>
        {staff && (
          <Field label="Status" className="col-span-2 lg:col-span-1">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Published and drafts</option>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Drafts</option>
              <option value="ARCHIVED">Archived</option>
            </Select>
          </Field>
        )}
      </Card>
      {editor && <NewCollection />}
      {items.isLoading && <Card className="p-4 text-sub">Loading…</Card>}
      {items.error && <ErrorText error={errorText(items.error)} />}
      {items.data && !items.data.length && (
        <Card>
          <Empty>{q || type || level || tag || collectionId ? 'Nothing matches those filters.' : 'The library is empty for now.'}</Empty>
        </Card>
      )}
      {groups.map((g) => (
        <section key={g.key} aria-label={g.title} className="flex flex-col gap-3">
          {(groups.length > 1 || g.key) && (
            <div className="flex flex-col gap-0.5">
              <h2 className="m-0 text-[17px] font-semibold">{g.title}</h2>
              {g.description && <span className="text-sm text-sub">{g.description}</span>}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {g.items.map((i) => (
              <ItemCard key={i.id} i={i} tz={tz} now={now} staff={staff} onTag={setTag} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function NewCollection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: '', description: '', level: '', sortOrder: '0' });
  const save = useMutation({
    mutationFn: () => api('/library/collections', { body: { title: f.title, description: f.description || null, level: f.level || null, sortOrder: Number(f.sortOrder) || 0 } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['library-collections'] });
      setF({ title: '', description: '', level: '', sortOrder: '0' });
      setOpen(false);
    },
  });
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer self-start text-sm font-semibold text-accent">
        + New collection or course
      </button>
    );
  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="m-0 text-[15px] font-semibold">New collection</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Field label="Name" className="sm:col-span-2">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. PSX foundations" />
        </Field>
        <Field label="Level">
          <Select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>
            <option value="">Mixed</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABEL[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Order" hint="lower comes first">
          <Input inputMode="numeric" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
        </Field>
        <Field label="Description (optional)" className="sm:col-span-4">
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
      </div>
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Create collection
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/** One video or document (strategies have their own page). */
export function LibraryItemPage() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const staff = isStaff(me.data?.roles);
  const editor = hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN');
  const tz = me.data?.timezone ?? 'UTC';
  const { data: item, error } = useQuery({ queryKey: ['library-item', id], queryFn: () => api<LibraryItem>(`/library/items/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['library-item', id] });
    void qc.invalidateQueries({ queryKey: ['library'] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  if (error)
    return (
      <>
        <PageHeader title="Library" back={<Link to="/library" className="text-[13px]">← Library</Link>} />
        <ErrorText error={errorText(error)} />
      </>
    );
  if (!item) return null;
  if (item.type === 'STRATEGY' && item.strategy) return <Navigate to={`/library/strategies/${encodeURIComponent(item.strategy.code)}`} replace />;

  return (
    <>
      <PageHeader
        title={item.title}
        sub={
          <>
            {TYPE_LABEL[item.type]}
            {item.level ? ` · ${LEVEL_LABEL[item.level]}` : ''} · by {item.createdBy.name}
            {item.publishedAt ? ` · ${fmtTime(item.publishedAt, tz, true)}` : ''}
            {item.collection ? ` · ${item.collection.title}` : ''}
          </>
        }
        back={<Link to="/library" className="text-[13px]">← Library</Link>}
        right={
          editor ? (
            <>
              <StatusPill status={item.status} />
              <Button onClick={() => nav(`/library/items/${item.id}/edit`)}>Edit</Button>
              <StatusActions item={item} onDone={refresh} />
            </>
          ) : undefined
        }
      />
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_560px] flex-col gap-3.5">
          {item.type === 'VIDEO_LINK' && <VideoPlayer item={item} />}
          {item.type === 'DOCUMENT' && <DocumentPanel item={item} staff={staff} />}
          {item.type === 'RECORDING' && item.recording && <LibraryRecordingPanel rec={item.recording} onChange={refresh} />}
          {item.description && (
            <Card className="p-4">
              <Prose text={item.description} />
            </Card>
          )}
          {editor && <RequiredLearningAdmin item={item} canAssign={editor} />}
          {!editor && staff && <RequiredLearningAdmin item={item} canAssign={false} />}
        </section>
        <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
          {(!editor || item.me.required) && <FinishedCard item={item} onChange={refresh} />}
          <Card className="flex flex-col gap-2 p-4 text-sm">
            {item.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {item.tags.map((t) => (
                  <span key={t} className="rounded-full border border-line2 bg-raised px-2.5 py-0.5 text-xs text-sub">
                    #{t}
                  </span>
                ))}
              </div>
            )}
            {item.onboarding && <span className="text-sub">Part of onboarding: every new trader gets this to read.</span>}
            {staff && item.audience && (
              <span className="text-sub">
                Shown to: <strong className="text-ink">{audienceText(item.audience)}</strong>
              </span>
            )}
            <span className="text-xs text-muted">Education only. General information, not personal financial advice.</span>
          </Card>
        </aside>
      </div>
    </>
  );
}
